import React from "react";
import { Card, Form, Select, Button, Row, Col, Space } from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";

const { Option } = Select;

export interface CalendarFilterValues {
  location_id?: number;
  shift_type?: "morning" | "day" | "night";
  status?: "scheduled" | "completed" | "canceled";
  user_ids?: number[];
  user_id?: number; // Add this for API filtering
}

interface ScheduleCalendarFilterProps {
  initialValues?: CalendarFilterValues;
  onFilter: (values: CalendarFilterValues) => void;
  onReset: () => void;
  locations?: Array<{ id: number; name: string }>;
  staff?: Array<{ id: number; name: string }>;
  canSeeAllSchedules?: boolean;
}

const ScheduleCalendarFilter: React.FC<ScheduleCalendarFilterProps> = ({
  initialValues,
  onFilter,
  onReset,
  locations = [],
  staff = [],
  canSeeAllSchedules = false,
}) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  // Show all staff since filtering is handled by the user_ids filter
  const availableStaff = staff;

  return (
    <Card className="mb-4">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={onFilter}
      >
        <Row gutter={16}>
          {canSeeAllLocations && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="location_id" label="Location">
                <Select placeholder="Select Location" allowClear>
                  {locations.map((location) => (
                    <Option key={location.id} value={location.id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={8}>
            <Form.Item name="shift_type" label="Shift Type">
              <Select placeholder="Select Shift Type" allowClear>
                <Option value="morning">Morning</Option>
                <Option value="day">Day</Option>
                <Option value="night">Night</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item name="status" label="Status">
              <Select placeholder="Select Status" allowClear>
                <Option value="scheduled">Scheduled</Option>
                <Option value="completed">Completed</Option>
                <Option value="canceled">Canceled</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Only show staff selection if user can see all schedules or if there are staff to show */}
          {canSeeAllSchedules && (
            <Col xs={24}>
              <Form.Item name="user_ids" label="Staff Members">
                <Select
                  placeholder={
                    canSeeAllSchedules
                      ? "Select Staff Members"
                      : "Your schedule filter"
                  }
                  allowClear
                  mode={canSeeAllSchedules ? "multiple" : "multiple"}
                  optionFilterProp="children"
                  showSearch={canSeeAllSchedules}
                  disabled={!canSeeAllSchedules && availableStaff.length <= 1}
                >
                  {availableStaff.map((person) => (
                    <Option key={person.id} value={person.id}>
                      {person.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>

        <div className="flex justify-end">
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
              Apply Filters
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default ScheduleCalendarFilter;
